import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import type { IUnidade } from '../types/unidade';
import { parseApiError } from '../utils/apiError';

interface IUseUnidadesParams {
  skip?: number;
  limit?: number;
  search?: string;
  enabled?: boolean;
}

interface IUseUnidadesResult {
  data: IUnidade[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const normalizeItems = (data: unknown): IUnidade[] => {
  if (Array.isArray((data as { items?: unknown })?.items)) {
    return (data as { items: IUnidade[] }).items;
  }
  return Array.isArray(data) ? (data as IUnidade[]) : [];
};

const useUnidades = ({
  skip = 0,
  limit = 50,
  search = '',
  enabled = true,
}: IUseUnidadesParams = {}): IUseUnidadesResult => {
  const [data, setData] = useState<IUnidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnidades = useCallback(async () => {
    if (!enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
      if (search.trim()) params.set('search', search.trim());

      const responseData = await api.get(`/unidades?${params.toString()}`);
      setData(normalizeItems(responseData));
    } catch (err) {
      setError(parseApiError(err, 'Erro ao listar unidades'));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, limit, search, skip]);

  useEffect(() => {
    void fetchUnidades();
  }, [fetchUnidades]);

  return {
    data,
    loading,
    error,
    refetch: fetchUnidades,
  };
};

export default useUnidades;
